import Vue from 'vue'
import { service } from './api'
import { INTEGRATIONS_STATUSES } from '../constants'
import { i18n } from '@/i18n'
import { TOAST_TYPES } from '@/constants'
import router from '@/router'
import { ROUTES_COMPANY_SETTINGS } from '@src/modules/company-settings/constants'

const store = {
    namespaced: true,

    state: {
        integrations: [],
        slack: {},
        slackOptions: [],
        dropbox: {},
        sharepoint: {},
        reconnectPopupShownThisSession: false
    },

    actions: {
        async getCompanyIntegrations ({ commit, rootGetters, dispatch }) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                const integrations = await service.getCompanyIntegrations(
                    companyId
                )

                commit('setIntegrations', integrations)
            } catch (err) {
                dispatch('errors/handleError', err, { root: true })
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async getSlackStatus ({ commit, rootGetters, dispatch }) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                const response = await service.getSlackStatus(companyId)

                commit('setSlackStatus', response)
            } catch (err) {
                dispatch('errors/handleError', err, { root: true })
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async finishOauthSlack ({ commit, rootGetters, dispatch }, payload) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                return await service.finishOauthSlack(companyId, payload)
            } catch (err) {
                dispatch('errors/handleError', err, { root: true })
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async getSlackOptions ({ commit, rootGetters, dispatch }) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                const response = await service.getSlackOptions(companyId)

                commit('setSlackOptions', response)
            } catch (err) {
                dispatch('errors/handleError', err, { root: true })
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async saveSlackOptions ({ commit, rootGetters, dispatch }, payload) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                return await service.saveSlackOptions(companyId, payload)
            } catch (err) {
                dispatch('errors/handleError', err, { root: true })
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async disconnectSlack ({ commit, rootGetters, dispatch }) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                return await service.disconnectSlack(companyId)
            } catch (err) {
                dispatch('errors/handleError', err, { root: true })
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async getDropboxStatus ({ commit, rootGetters, dispatch }) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                const response = await service.getDropboxStatus(companyId)

                commit('setDropboxStatus', response)
            } catch (err) {
                dispatch('errors/handleError', err, { root: true })
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async finishOauthDropbox ({ commit, rootGetters, dispatch }, payload) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                return await service.finishOauthDropbox(companyId, payload)
            } catch (err) {
                dispatch('errors/handleError', err, { root: true })
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async disconnectDropbox ({ commit, rootGetters, dispatch }) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                return await service.disconnectDropbox(companyId)
            } catch (err) {
                dispatch('errors/handleError', err, { root: true })
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async getSharepointStatus ({ commit, rootGetters, dispatch }) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                const response = await service.getSharepointStatus(companyId)

                commit('setSharepointStatus', response)
            } catch (err) {
                dispatch('errors/handleError', err, { root: true })
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async finishOauthSharepoint (
            { commit, rootGetters, dispatch },
            payload
        ) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                return await service.finishOauthSharepoint(companyId, payload)
            } catch (err) {
                // 422 with structured `error_code` means the component will
                // render its own failure modal (site_not_found / site_not_
                // granted / validation_failed). Suppress the generic toast
                // so the user sees one error surface, not two. Same pattern
                // as saveSharepointOptions (Phase 1d unified the response
                // shape across all three SharePoint endpoints).
                const errorCode = err?.response?.data?.error_code
                if (!errorCode) {
                    dispatch('errors/handleError', err, { root: true })
                }
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async getSharepointAuthUrl (
            { commit, rootGetters, dispatch },
            payload
        ) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                return await service.getSharepointAuthUrl(companyId, payload)
            } catch (err) {
                // Same structured-error suppression as finishOauthSharepoint
                // and saveSharepointOptions: a 422 with `error_code` means
                // the backend URL-validator rejected the URL at /auth-url
                // time (admin/onedrive/teams host, invalid path, etc.). The
                // component routes back to Phase 2 and renders the inline
                // error from the `reason` field — generic toast would be
                // a duplicate surface.
                const errorCode = err?.response?.data?.error_code
                if (!errorCode) {
                    dispatch('errors/handleError', err, { root: true })
                }
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async saveSharepointOptions (
            { commit, rootGetters, dispatch },
            payload
        ) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                return await service.saveSharepointOptions(companyId, payload)
            } catch (err) {
                // 422 with a structured `error_code` means the component will
                // render its own error UX (site_not_granted, site_not_found,
                // validation_failed). Skip the generic toast to avoid a
                // duplicate error surface.
                const errorCode = err?.response?.data?.error_code
                if (!errorCode) {
                    dispatch('errors/handleError', err, { root: true })
                }
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        async disconnectSharepoint ({ commit, rootGetters, dispatch }) {
            commit('SET_APP_LOADING', true, { root: true })

            try {
                const companyId = rootGetters['company/company'].id

                return await service.disconnectSharepoint(companyId)
            } catch (err) {
                dispatch('errors/handleError', err, { root: true })
                throw err
            } finally {
                commit('SET_APP_LOADING', false, { root: true })
            }
        },

        // Render a persistent toast per CT-378 sync warning surfaced by BE actions like
        // dms/renameFile. The local action (rename, upload, delete) succeeded, but the
        // external mirror (SharePoint / Dropbox) diverged — the toast tells the user what
        // failed and how to recover. Triggered via dispatch('integrations/showSyncWarnings',
        // [{type, reason}], { root: true }).
        //
        // Toast structure (vue-toasted):
        // - duration: null → persistent until user closes or clicks Retry.
        // - action: "Retry" button. For now the button just dismisses the toast — a real
        //   per-integration retry endpoint is tracked in CT-378 state file under
        //   "Post-connect health visibility" follow-up batch. Wiring the button stub keeps
        //   the toast UI shape stable for that follow-up.
        showSyncWarnings (_, warnings) {
            if (!warnings || warnings.length === 0) {
                return
            }
            // Retry navigation must match the failing provider — otherwise a Dropbox sync
            // failure routes the user to the SharePoint settings screen with no idea why.
            // Unknown providers fall back to the generic Integrations index.
            const routeByProvider = {
                sharepoint: ROUTES_COMPANY_SETTINGS.INTEGRATIONS_SHAREPOINT.name,
                dropbox: ROUTES_COMPANY_SETTINGS.INTEGRATIONS_DROPBOX.name,
                slack: ROUTES_COMPANY_SETTINGS.INTEGRATIONS_SLACK.name
            }
            warnings.forEach(warning => {
                const providerType = String(warning?.type ?? 'unknown').toLowerCase()
                const providerKey = providerType.toUpperCase()
                const reasonKey = String(warning?.reason ?? 'unknown').toUpperCase()
                const i18nKey =
                    `INTEGRATIONS.SYNC_WARNINGS.${providerKey}.${reasonKey}`
                const fallback = i18n.t(
                    'INTEGRATIONS.SYNC_WARNINGS.GENERIC_FAILURE',
                    { provider: providerKey, reason: reasonKey }
                )
                const message = i18n.te(i18nKey)
                    ? i18n.t(i18nKey)
                    : fallback
                const targetRoute =
                    routeByProvider[providerType] ??
                    ROUTES_COMPANY_SETTINGS.INTEGRATIONS.name

                Vue.toasted.show(message, {
                    type: TOAST_TYPES.ERROR,
                    duration: null,
                    action: [
                        {
                            text: i18n.t(
                                'INTEGRATIONS.SYNC_WARNINGS.RETRY_ACTION'
                            ),
                            onClick: (e, toastObject) => {
                                // Future: dispatch a per-integration retry endpoint to re-
                                // sync the failed item. Until that's built, navigating the
                                // admin to the failing provider's settings page is the
                                // actionable next step.
                                router.push({ name: targetRoute }).catch(() => {})
                                toastObject.goAway(0)
                            }
                        },
                        {
                            text: i18n.t(
                                'INTEGRATIONS.SYNC_WARNINGS.DISMISS_ACTION'
                            ),
                            onClick: (e, toastObject) => {
                                toastObject.goAway(0)
                            }
                        }
                    ]
                })
            })
        }
    },

    mutations: {
        setIntegrations (state, integrations) {
            state.integrations = integrations
        },

        setSlackStatus (state, slack) {
            state.slack = slack
        },

        setSlackOptions (state, slackOptions) {
            state.slackOptions = slackOptions
        },

        setDropboxStatus (state, dropbox) {
            state.dropbox = dropbox
        },

        setSharepointStatus (state, sharepoint) {
            state.sharepoint = sharepoint
        },

        setSharepointReconnectPopupShown (state) {
            state.reconnectPopupShownThisSession = true
        },

        resetSharepointReconnectPopupSession (state) {
            state.reconnectPopupShownThisSession = false
            state.sharepoint = {}
        }
    },

    getters: {
        // Trigger policy lives here so future flips (admin-only, every-load, localStorage-backed) stay a one-line change.
        shouldShowSharepointReconnectPopup (state) {
            return (
                state.sharepoint?.status === INTEGRATIONS_STATUSES.IDLE &&
                !state.reconnectPopupShownThisSession
            )
        }
    }
}

export { store }
